// @spec BCP-STR-052
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorBCPOverview } from '@/features/auditor-access/components/AuditorBCPOverview';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/bcp')({
  component: AuditorBCPPage,
});

/**
 * Auditor BCP overview page.
 *
 * Displays a read-only view of business continuity data for an organization
 * that the auditor has access to review.
 */
function AuditorBCPPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: `/auditor/access/${currentAccess.accessId}/bcp` })}
          >
            {t('routes.goToYourBCPOverview')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'bcp_plans'
  if (!currentAccess.accessScope.includes('bcp_plans')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedBCP')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleNavigateToPlans = () => {
    void navigate({ to: `/auditor/access/${accessId}/bcp/plans` });
  };

  const handleNavigateToExercises = () => {
    void navigate({ to: `/auditor/access/${accessId}/bcp/exercises` });
  };

  const handleNavigateToFindings = () => {
    void navigate({ to: `/auditor/access/${accessId}/bcp/findings` });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          <AuditorBCPOverview
            accessId={accessId}
            organizationName={currentAccess.name}
            onNavigateToPlans={handleNavigateToPlans}
            onNavigateToExercises={handleNavigateToExercises}
            onNavigateToFindings={handleNavigateToFindings}
          />
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
